import Head from "next/head";

import { Faqs } from "@/components/LandingPage/Faqs";
import { Footer } from "@/components/LandingPage/Footer";
import { Header } from "@/components/LandingPage/Header";
import { DeveloperApiPreview } from "@/components/LandingPage/DeveloperApiPreview";
import { Hero } from "@/components/LandingPage/Hero";
import { Pricing } from "@/components/LandingPage/Pricing";
import { PrimaryFeatures } from "@/components/LandingPage/PrimaryFeatures";
import { SecondaryFeatures } from "@/components/LandingPage/SecondaryFeatures";
import { UseCases } from "@/components/LandingPage/UseCases";
import JSONStorageInterface from "@/components/JSONStorage/JSONStorageInterface";
import { useEffect } from "react";
import { useRouter } from "next/router";
import analytics from "@/utils/analytics/analytics";
import GlobeWithStats from "@/components/GlobeWithStats/GlobeWithStats";
import { Container } from "@/components/LandingPage/Container";
import { Integration } from "@/components/Integration";
import { getStatsForLandingPage } from "@/controllers/stats";
import { InferGetStaticPropsType } from "next";

export default function Home(
  props: InferGetStaticPropsType<typeof getStaticProps>
) {
  const { recordsAnalyticsCount, recordsCount, globeMarkers } = props;

  useEffect(() => {
    analytics.track("view_marketing_page");
  }, []);

  // TODO
  // COnnecter paiement
  // Upgrade compte via page billing
  // Erreur lighthouse

  return (
    <div className="bg-[#1E1E1E]">
      <Head>
        <title>myJson - Secure JSON Storage API | RESTful JSON Database Service</title>
        <meta name="description" content="Store, manage, and version JSON data with our secure REST API. Perfect for prototyping, CMS, IoT, and web applications. No subscription required, pay-as-you-go pricing." />
        <meta name="keywords" content="JSON storage, JSON API, REST API, JSON database, JSON hosting, JSON service, API storage, JSON management, JSON versioning, JSON analytics" />
        <meta name="author" content="myJson.online" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://myjson.online/" />
        <meta property="og:title" content="myJson - Secure JSON Storage API | RESTful JSON Database Service" />
        <meta property="og:description" content="Store, manage, and version JSON data with our secure REST API. Perfect for prototyping, CMS, IoT, and web applications. No subscription required, pay-as-you-go pricing." />
        <meta property="og:image" content="https://myjson.online/og-image.png" />
        <meta property="og:site_name" content="myJson.online" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://myjson.online/" />
        <meta property="twitter:title" content="myJson - Secure JSON Storage API | RESTful JSON Database Service" />
        <meta property="twitter:description" content="Store, manage, and version JSON data with our secure REST API. Perfect for prototyping, CMS, IoT, and web applications." />
        <meta property="twitter:image" content="https://myjson.online/og-image.png" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://myjson.online/" />
        
        {/* Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "myJson - JSON Storage API",
              "description": "Secure REST API for storing, managing, and versioning JSON data. Perfect for developers building prototypes, CMS, IoT applications, and web dashboards.",
              "url": "https://myjson.online",
              "applicationCategory": "DeveloperApplication",
              "operatingSystem": "Any",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD",
                "description": "Free tier with 500 credits, pay-as-you-go pricing"
              },
              "featureList": [
                "JSON Storage & Management",
                "RESTful API Access",
                "JSON Versioning & Rollback",
                "Private Collections with API Keys",
                "Real-time Analytics Dashboard",
                "JSON Formatting Tools",
                "JSON Diff Checker",
                "Secure Data Protection"
              ],
              "audience": {
                "@type": "Audience",
                "audienceType": "Developers"
              }
            })
          }}
        />
      </Head>

      <div className="hero-radial-background">
        <Header dark={false} />
        <Hero />
        <DeveloperApiPreview />
        <div className="-mt-32">
          <GlobeWithStats
            recordAnalyticsCount={recordsAnalyticsCount}
            recordsCount={recordsCount}
            globeMarkers={globeMarkers}
          />
        </div>
      </div>
      
      {/* Quick JSON Storage Section */}
      <div id="json-storage-section" className="py-16 bg-gradient-to-b from-[#1E1E1E] to-[#0F0F0F]">
        <JSONStorageInterface />
      </div>
      <div className="landing-page-diamond-gradient">
        <PrimaryFeatures />
        <UseCases />
        <Pricing />
      </div>
      {/* <Integration /> */}
      <Faqs />
      <Footer />
    </div>
  );
}

export async function getStaticProps() {
  const { recordsAnalyticsCount, recordsCount, globeMarkers } =
    await getStatsForLandingPage();
  return {
    props: {
      recordsAnalyticsCount,
      recordsCount,
      globeMarkers,
    },
    revalidate: 1800, // 1800 seconds (30 minutes)
  };
}
